import { useMemo, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

interface CalendarGridProps<T> {
  currentMonth: Date;
  onMonthChange: (month: Date) => void;
  items: T[];
  getItemDate: (item: T) => string | null;
  renderItem: (item: T) => ReactNode;
  onDateClick?: (date: Date) => void;
  maxItemsPerDay?: number;
}

export function CalendarGrid<T>({
  currentMonth,
  onMonthChange,
  items,
  getItemDate,
  renderItem,
  onDateClick,
  maxItemsPerDay = 3,
}: CalendarGridProps<T>) {
  // Build calendar grid
  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startOffset = firstDay.getDay();

    const days: { date: Date; isCurrentMonth: boolean }[] = [];

    // Previous month padding
    for (let i = startOffset - 1; i >= 0; i--) {
      const d = new Date(year, month, -i);
      days.push({ date: d, isCurrentMonth: false });
    }

    // Current month
    for (let d = 1; d <= lastDay.getDate(); d++) {
      days.push({ date: new Date(year, month, d), isCurrentMonth: true });
    }

    // Next month padding (fill to 42 = 6 rows)
    while (days.length < 42) {
      const d = new Date(year, month + 1, days.length - startOffset - lastDay.getDate() + 1);
      days.push({ date: d, isCurrentMonth: false });
    }

    return days;
  }, [currentMonth]);

  // Items by date
  const itemsByDate = useMemo(() => {
    const map = new Map<string, T[]>();
    for (const item of items) {
      const dateStr = getItemDate(item);
      if (!dateStr) continue;
      const key = dateStr.split('T')[0];
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(item);
    }
    return map;
  }, [items, getItemDate]);

  const today = new Date().toISOString().split('T')[0];

  return (
    <div>
      {/* Month navigation */}
      <div className="flex items-center gap-2 mb-4 justify-end">
        <button
          onClick={() => onMonthChange(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}
          className="p-2 hover:bg-surface-subtle rounded-lg transition-colors"
        >
          <ChevronLeft size={18} className="text-muted" />
        </button>
        <span className="text-sm font-semibold text-text min-w-[140px] text-center">
          {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </span>
        <button
          onClick={() => onMonthChange(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}
          className="p-2 hover:bg-surface-subtle rounded-lg transition-colors"
        >
          <ChevronRight size={18} className="text-muted" />
        </button>
        <button
          onClick={() => onMonthChange(new Date(new Date().getFullYear(), new Date().getMonth(), 1))}
          className="ml-2 px-3 py-1.5 text-xs font-medium text-muted bg-surface-subtle rounded-lg hover:bg-surface-subtle"
        >
          Today
        </button>
      </div>

      {/* Calendar Grid */}
      <div className="bg-white rounded-xl border border-line overflow-hidden">
        {/* Day headers */}
        <div className="grid grid-cols-7 border-b border-line">
          {DAYS.map(day => (
            <div key={day} className="px-2 py-2 text-xs font-semibold text-muted text-center uppercase tracking-wider bg-surface-muted">
              {day}
            </div>
          ))}
        </div>

        {/* Date cells */}
        <div className="grid grid-cols-7">
          {calendarDays.map(({ date, isCurrentMonth }, idx) => {
            const dateStr = date.toISOString().split('T')[0];
            const dayItems = itemsByDate.get(dateStr) || [];
            const isToday = dateStr === today;

            return (
              <div
                key={idx}
                onClick={onDateClick ? () => onDateClick(date) : undefined}
                className={`min-h-[100px] border-b border-r border-line p-1.5
                  ${!isCurrentMonth ? 'bg-surface-muted/50' : ''}
                  ${isToday ? 'bg-coral-50/30' : ''}
                  ${onDateClick ? 'cursor-pointer hover:bg-surface-muted/80' : ''}`}
              >
                <div className={`text-xs font-medium mb-1 px-1
                  ${isToday ? 'text-coral-600' : isCurrentMonth ? 'text-text' : 'text-subtle'}`}
                >
                  {date.getDate()}
                </div>
                <div className="space-y-0.5">
                  {dayItems.slice(0, maxItemsPerDay).map((item, i) => (
                    <div key={i}>{renderItem(item)}</div>
                  ))}
                  {dayItems.length > maxItemsPerDay && (
                    <div className="text-[9px] text-subtle px-1.5">
                      +{dayItems.length - maxItemsPerDay} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
